﻿import React, { useState } from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import {
    RemovalProposalStatus,
    ThirdPartyPayoffsSection,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { ThirdPartyPayoffRow, ThirdPartyPayoffRowProps } from './ThirdPartyPayoffRow';
import {
    THIRD_PARTY_PAYOFFS_AUTOSAVE_TOAST_CONTAINER_ID,
    ThirdPartyPayoffRowSaveHandler,
    useThirdPartyPayoffsAutosave,
} from './useThirdPartyPayoffsAutosave';
import { DataTableCellUploadPopup } from '../../../containers/DataRequest/DataTableCellUploadPopup/DataTableCellUploadPopup';
import '../../AutoSaveToast/AutoSaveToast.scss';
import './ThirdPartyPayoffs.scss';

const APPENDED_COLUMN_COUNT = 9;

export interface ThirdPartyPayoffsProps
    extends Omit<
        ThirdPartyPayoffRowProps,
        | 'row'
        | 'columns'
        | 'fileColumnId'
        | 'viewerParty'
        | 'showRemovalColumn'
        | 'onOpenFilePopup'
        | 'autosave'
    > {
    section: ThirdPartyPayoffsSection;
    onSaveRow: ThirdPartyPayoffRowSaveHandler;
    onAddRow: () => void;
    onDaqiFilesChanged: () => void;
}

export const ThirdPartyPayoffs = ({
    section,
    onSaveRow,
    onOpenWireInstructions,
    onSupportingDocumentUpload,
    onSupportingDocumentDelete,
    onSupportingDocumentDownload,
    onApprovalChange,
    onBuyerAssumptionChange,
    onPayoffAmountChange,
    onOpenContractDocument,
    onOpenRemovalProposalModal,
    onRespondToRemoval,
    onAddRow,
    onDaqiFilesChanged,
}: ThirdPartyPayoffsProps) => {
    const autosave = useThirdPartyPayoffsAutosave(section.rows, onSaveRow);
    const [filePopup, setFilePopup] = useState<{ tableRowId: number; title: string } | null>(null);
    const showRemovalColumn = section.rows.some(
        (row) =>
            row.capabilities.canProposeRemoval ||
            row.removalProposal?.status === RemovalProposalStatus.Pending
    );
    const appendedColumnCount = showRemovalColumn
        ? APPENDED_COLUMN_COUNT
        : APPENDED_COLUMN_COUNT - 1;

    if (!section.dataTableId || section.columns.length === 0) {
        return (
            <div className="ff-payoffs">
                <p className="ff-payoffs__description">
                    No vendor-contracts table is configured for this engagement yet.
                </p>
            </div>
        );
    }

    return (
        <div className="ff-payoffs">
            <p className="ff-payoffs__description">
                The following obligations of the Seller are to be paid off at closing.
            </p>

            <div className="ff-payoffs__scroll">
                <table className="table table-striped ff-payoffs__table">
                    <thead>
                        <tr>
                            {section.columns.map((column) => (
                                <th key={column.columnId}>{column.name}</th>
                            ))}
                            <th>Buyer Assumption</th>
                            <th>Payoff Letter</th>
                            <th>Pay-Off Date</th>
                            <th>Pay-Off Amount</th>
                            <th>Per Diem</th>
                            <th>Wire Instructions</th>
                            <th className="is-centered">Buyer Approval</th>
                            <th className="is-centered">Seller Approval</th>
                            {showRemovalColumn && <th className="is-centered">Removal</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {section.rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={section.columns.length + appendedColumnCount}
                                    className="ff-payoffs__empty">
                                    No third-party pay-offs have been recorded for this engagement yet.
                                </td>
                            </tr>
                        )}

                        {section.rows.map((row) => (
                            <ThirdPartyPayoffRow
                                key={row.tableRowId}
                                row={row}
                                columns={section.columns}
                                fileColumnId={section.fileColumnId}
                                viewerParty={section.viewerParty}
                                showRemovalColumn={showRemovalColumn}
                                autosave={autosave}
                                onOpenWireInstructions={onOpenWireInstructions}
                                onSupportingDocumentUpload={onSupportingDocumentUpload}
                                onSupportingDocumentDelete={onSupportingDocumentDelete}
                                onSupportingDocumentDownload={onSupportingDocumentDownload}
                                onApprovalChange={onApprovalChange}
                                onBuyerAssumptionChange={onBuyerAssumptionChange}
                                onPayoffAmountChange={onPayoffAmountChange}
                                onOpenContractDocument={onOpenContractDocument}
                                onOpenRemovalProposalModal={onOpenRemovalProposalModal}
                                onRespondToRemoval={onRespondToRemoval}
                                onOpenFilePopup={(tableRowId, title) => setFilePopup({ tableRowId, title })}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {section.canAddRow && (
                <div className="ff-payoffs__actions">
                    <button type="button" className="btn btn-primary" onClick={onAddRow}>
                        Add New Item
                    </button>
                </div>
            )}

            {filePopup && section.dataRequestProjectId && section.dataTableId && section.fileColumnId != null && (
                <DataTableCellUploadPopup
                    projectId={section.dataRequestProjectId}
                    tableId={section.dataTableId}
                    columnId={section.fileColumnId}
                    rowId={filePopup.tableRowId}
                    isReadonlyMode={
                        !section.rows.find((r) => r.tableRowId === filePopup.tableRowId)?.capabilities
                            .canEditCells
                    }
                    isAdminView={false}
                    close={() => setFilePopup(null)}
                    onSuccessfulUpload={onDaqiFilesChanged}
                    title={filePopup.title}
                />
            )}

            <ToastContainer
                containerId={THIRD_PARTY_PAYOFFS_AUTOSAVE_TOAST_CONTAINER_ID}
                position="bottom-right"
                transition={Slide}
                className="auto-save-toast-container"
                newestOnTop={false}
            />
        </div>
    );
};
